import React from 'react';
import styled from 'styled-components';
import {Checkbox, ComboBox, Detail, Heading, Text} from '@forecast-it/design-system';
import {TeamFilterValue} from './featureFlagRows';

const ToolbarWrapper = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	margin-bottom: 20px;
`;

const TitleBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

// Pushed hard against the right edge and never allowed to shrink, so the controls keep their sizes
// and the title paragraph absorbs any spare width instead.
const Controls = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: flex-end;
	gap: 20px;
	margin-left: auto;
	flex: none;
`;

const ControlBlock = styled.div<{$width?: string}>`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: none;
	${props => (props.$width ? `width: ${props.$width};` : '')}
`;

// The checkbox's label is one short phrase and must stay on a single line; left to wrap in a narrow
// flex column it breaks after every word and each word then ellipses on its own.
const CheckboxBlock = styled(ControlBlock)`
	white-space: nowrap;
`;

const TEAM_FILTER_WIDTH = '180px';
const COMPANY_PICKER_WIDTH = '240px';

interface ComboOption {
	id: string;
	name: string;
}

export interface FeatureFlagsToolbarProps {
	visibleCount: number;
	totalCount: number;
	teamFilter: TeamFilterValue;
	// The design-system ComboBox can emit `null` (its selection clears whenever the input text is
	// cleared, including by backspacing it out), so the type here tells the truth about what a
	// handler must accept even though callers typically normalise it away immediately.
	onTeamFilterChange: (value: TeamFilterValue | null) => void;
	showRolledOut: boolean;
	onShowRolledOutChange: (value: boolean) => void;
	// Mutable arrays, matching the design-system ComboBox's `items?: T[]` prop — a readonly array
	// is not assignable there, and copying on every render just to satisfy the type would be worse.
	companies: ComboOption[];
	onCompanySelected: (companyId: string | null) => void;
	teamFilterOptions: Array<{id: TeamFilterValue; name: string}>;
	// Changing this value remounts the company picker, clearing its typed text and selection. The
	// picker is an action trigger rather than a filter, so the caller bumps this once it has finished
	// acting on a pick, leaving the control ready for the next one.
	companyPickerResetToken: number;
}

export function FeatureFlagsToolbar({
	visibleCount,
	totalCount,
	teamFilter,
	onTeamFilterChange,
	showRolledOut,
	onShowRolledOutChange,
	companies,
	onCompanySelected,
	teamFilterOptions,
	companyPickerResetToken,
}: FeatureFlagsToolbarProps): JSX.Element {
	return (
		<ToolbarWrapper>
			<TitleBlock>
				<Heading>Feature flags</Heading>
				<Detail>
					{`${visibleCount} of ${totalCount} flags · rolled-out flags live in code and have no detail page`}
				</Detail>
			</TitleBlock>
			<Controls>
				<ControlBlock $width={TEAM_FILTER_WIDTH}>
					{/* Selection is controlled by the caller's state, since this is a persistent filter.
					    The option list is passed as `defaultItems` rather than `items` — see the company
					    picker below for why that distinction decides whether typing filters the list. */}
					<ComboBox
						label={'Filter by team'}
						width={ComboBox.WIDTH.FLEX}
						value={teamFilter}
						onChange={(value: TeamFilterValue | null) => onTeamFilterChange(value)}
						defaultItems={teamFilterOptions}
					>
						{(item: {id: TeamFilterValue; name: string}) => (
							<ComboBox.Item textValue={item.name}>
								<Text>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</ControlBlock>
				<CheckboxBlock>
					{/* The checkbox carries its own visible label, so there is no separate caption above
					    it as there is for the two comboboxes. */}
					<Checkbox
						labelText={'Show rolled out'}
						checked={showRolledOut}
						onChange={() => onShowRolledOutChange(!showRolledOut)}
					/>
				</CheckboxBlock>
				<ControlBlock $width={COMPANY_PICKER_WIDTH}>
					{/* An action trigger rather than a filter: picking a company opens that company's
					    modal, and the control then resets by remounting on the key below.

					    Two deliberate choices here, both of which look like omissions:

					    - No `value`. That prop becomes the underlying combobox's controlled selected
					      key, and a key that never changes suppresses the close-on-select behaviour,
					      which fires on a selection change.
					    - `defaultItems`, not `items`. Passing `items` makes the option list controlled,
					      and a controlled list is never run through the built-in text filter — the
					      caller is expected to filter it instead. With `defaultItems` the list filters
					      as you type, which is essential at this list's size. */}
					<ComboBox
						key={companyPickerResetToken}
						label={'Check company'}
						width={ComboBox.WIDTH.FLEX}
						placeholder={'Search companies'}
						onChange={(companyId: string | null) => onCompanySelected(companyId)}
						defaultItems={companies}
					>
						{(item: ComboOption) => (
							<ComboBox.Item textValue={item.name}>
								<Text>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</ControlBlock>
			</Controls>
		</ToolbarWrapper>
	);
}
